import { useId, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx.js';

export type ButtonVariant =
  'primary' | 'secondary' | 'quiet' | 'danger' | 'onPanel' | 'tint' | 'tintWarn' | 'tintDanger';
export type ButtonSize = 'md' | 'sm' | 'xs';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks clicks, without the control losing its label. */
  busy?: boolean;
  /**
   * Why the button is unavailable. Disabled controls are invisible to screen
   * readers and give no reason, so a blocked action stays focusable and
   * explains itself instead (PLAN §13.3 wants a tappable list of what is
   * missing, not a dead button).
   */
  blockedReason?: string;
  /**
   * Run when a blocked button is pressed. Without it, pressing a blocked
   * control does nothing at all and the owner is left guessing — the composer
   * used to swallow a "Post now" tap in silence. Pass something that reveals
   * the reason, e.g. opening the list of problems.
   */
  onBlockedClick?: () => void;
  iconLeft?: ReactNode;
}

const base =
  'aw-press inline-flex items-center justify-center gap-2 rounded-aw font-semibold ' +
  'transition-colors select-none disabled:cursor-not-allowed';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-on-accent hover:brightness-110 active:brightness-95',
  secondary: 'border border-control text-ink bg-surface hover:bg-raise',
  quiet: 'text-muted hover:text-ink hover:bg-raise',
  // Quiet until reached for. A destructive action outlined in red is the
  // loudest control on the screen, which is backwards: Disconnect and Delete
  // are the things you least often want. It turns red on hover and focus,
  // before any click. Pair it with ConfirmButton below for anything that
  // cannot be undone — an earlier version of this comment claimed every
  // caller already did, and two of them deleted on the first press.
  danger:
    'border border-line text-muted bg-surface hover:border-bad hover:bg-bad-soft hover:text-bad ' +
    'focus-visible:border-bad focus-visible:text-bad',
  // For the black panel, where `secondary` would paint a white box on black
  // and `quiet` would hand back muted grey text that the panel swallows.
  // Both of the app's panel surfaces are the same black in either theme, so
  // this one variant does not need a dark-mode counterpart.
  onPanel:
    'border border-on-panel/40 text-on-panel bg-transparent hover:bg-on-panel/12 ' +
    'focus-visible:border-on-panel',

  // Borderless, a soft wash of one hue each. Three outlined boxes in a row
  // read as three boxes; a wash lets the card stay one object while colour
  // does the telling-apart that size used to. The hues are the app's own
  // semantic set, which already carries dark-theme values — so these invert
  // with everything else instead of staying a light smudge on black.
  tint: 'bg-slate-soft text-slate hover:brightness-95 active:brightness-90',
  tintWarn: 'bg-amber-soft text-amber hover:brightness-95 active:brightness-90',
  tintDanger: 'bg-bad-soft text-bad hover:brightness-95 active:brightness-90',
};

// min-h keeps the 44px touch target PLAN §13.1 asks for, even at the small size
// where the visual height is lower.
const sizes: Record<ButtonSize, string> = {
  md: 'min-h-[var(--aw-tap)] px-4 text-sm',
  sm: 'min-h-[36px] px-3 text-sm',
  // Half the painted height of `md`, with the touch target unchanged. The
  // pseudo-element adds 7px of hit area above and below the 30px box, which
  // comes back to the 44px `--aw-tap` PLAN §13.1 asks for — shrinking the
  // box is a look, shrinking the target is a phone you keep missing.
  xs: 'relative h-[30px] px-2.5 text-xs after:absolute after:inset-x-0 after:-inset-y-[7px] after:content-[""]',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  busy = false,
  blockedReason,
  onBlockedClick,
  iconLeft,
  className,
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps) {
  const blocked = Boolean(blockedReason);
  const reasonId = useId();

  // The reason was accepted by every caller and shown to nobody: it set a
  // boolean and was then dropped, while the doc comment above promised the
  // control "explains itself instead". Now it is both announced — through
  // aria-describedby, which is why it is rendered rather than put in `title`
  // — and visible on hover for a mouse.
  const describedBy = blocked
    ? [rest['aria-describedby'], reasonId].filter(Boolean).join(' ')
    : rest['aria-describedby'];

  return (
    <button
      type="button"
      // Not `disabled`: a blocked button must stay reachable so the reason can
      // be announced. aria-disabled tells assistive tech it will not act.
      aria-disabled={blocked || busy || disabled ? true : undefined}
      aria-describedby={describedBy}
      title={blocked ? blockedReason : rest.title}
      disabled={disabled && !blocked}
      onClick={(event) => {
        if (blocked || busy || disabled) {
          event.preventDefault();
          if (blocked) onBlockedClick?.();
          return;
        }
        onClick?.(event);
      }}
      className={cx(
        base,
        variants[variant],
        sizes[size],
        (blocked || disabled) && 'opacity-55',
        busy && 'cursor-progress',
        className,
      )}
      {...rest}
    >
      {busy ? <Spinner /> : iconLeft}
      <span>{children}</span>
      {blocked && (
        <span id={reasonId} className="sr-only">
          {blockedReason}
        </span>
      )}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
    />
  );
}

/**
 * A destructive action that asks once.
 *
 * The first press arms it and changes the label; the second does the thing.
 * Moving focus away, or Escape, disarms it — so an armed button cannot be
 * left lying in wait for an unrelated click later.
 *
 * A sheet would be heavier than these choices deserve, and heavier than the
 * alternative that was actually shipped: deleting a media file or an account
 * group fired on the first press, with no confirmation and no undo. Two
 * presses on the same control is the smallest honest gate.
 *
 * Not a replacement for a sheet everywhere — something that destroys many
 * things at once, or cannot be re-made, still deserves a real dialog.
 */
export interface ConfirmButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /** Resting label, e.g. "Delete". */
  children: ReactNode;
  /** Armed label. Phrase it as the question: "Delete for good?" */
  confirmLabel: ReactNode;
  onConfirm: () => void;
}

export function ConfirmButton({
  children,
  confirmLabel,
  onConfirm,
  variant = 'danger',
  ...rest
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);

  return (
    <Button
      variant={variant}
      {...rest}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        onConfirm();
      }}
      onBlur={() => {
        setArmed(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && armed) {
          event.stopPropagation();
          setArmed(false);
        }
      }}
    >
      {armed ? confirmLabel : children}
    </Button>
  );
}
